import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import moment from 'moment';
import { clientService } from '../../_services/client.service';
import { hairdresserService } from '../../_services/hairdresser.service';
import toast from '../shared/Toast';
import './ClientNotes.css';

/**
 * NOTES FOLLOW THE BOOK (Charlie, 2026-10-16).
 *
 * A client's notes belong to the book they're in: the salon's book, or a
 * self-employed stylist's own book. Sharing your book shares your notes. No
 * per-note toggles.
 *
 * The server decides everything (lib/module/v2/salonClient/clientNotes.js):
 *   notes.mine          the book you write in (null when you have none)
 *   notes.others        every other book you may see, each with `editable`
 *   notes.privateBooks  salon side only: stylists with private notes here,
 *                       NAMES ONLY (Charlie, 2026-10-25) — so a stylist at
 *                       the salon computer knows their note isn't lost
 * A private note's text never arrives.
 *
 * Layout (40+ stylists at a salon must not clutter it): your box and the
 * business's notes in full; every stylist's notes behind ONE line —
 * "Notes from 6 stylists · 2 you can read" — that drills in, in place.
 *
 * Never lose typed text: every keystroke is kept as a draft in
 * sessionStorage (per client + book) until it is saved or discarded, so a
 * closed panel, a switched tab or a refresh brings it back; a page unload
 * with unsaved text asks first.
 *
 * Old BE (no client/notes endpoint and no `notes` on the client): falls back
 * to the single legacy `note`, saved through client/edit as before.
 *
 * Props
 *   clientId       required
 *   notes          structured notes already in hand (client detail payload)
 *   legacyNote     the old single note, for the fallback
 *   canEditLegacy  edit permission for the legacy fallback only
 *   compact        tighter layout for the booking panel
 *   onSaved(res)   after any successful save / restore ({ note, notes })
 */

const DRAFT_PREFIX = 'cnotes:draft:';

function draftKey(clientId, book) { return `${DRAFT_PREFIX}${clientId}:${book}`; }
function readDraft(clientId, book) {
  try { const v = sessionStorage.getItem(draftKey(clientId, book)); return v === null ? null : v; } catch (e) { return null; }
}
function writeDraft(clientId, book, text) {
  try { sessionStorage.setItem(draftKey(clientId, book), text); } catch (e) { /* storage blocked: unload guard still applies */ }
}
function clearDraft(clientId, book) {
  try { sessionStorage.removeItem(draftKey(clientId, book)); } catch (e) { /* ignore */ }
}

function whoFromLabel(label) {
  const m = /^(.*)'s notes$/.exec(label || '');
  return m ? m[1] : null;
}

// The business's own name, never "salon": Beu serves barbers and more
// (Charlie, 2026-10-25). The server sends it with the notes.
function teamOf(biz) { return biz ? `the ${biz} team` : 'the team'; }
function businessBookLabel(biz) { return biz ? `${biz} notes` : 'Team notes'; }

/** "Who can see this" for your own box. */
function mineCaption(mine, biz) {
  if (!mine) return '';
  if (mine.book === 'salon' || mine.visibleTo === 'salon_team') return `Visible to ${teamOf(biz)}`;
  if (mine.visibleTo === 'you_and_salon_team') return `Visible to you and ${teamOf(biz)}`;
  if (mine.salonSeesItExists) return `Only you can see what you write. ${biz || 'The team'} can see that you have notes here, not what they say.`;
  return 'Only you can see this';
}

function initialsOf(name) {
  return String(name || '?').split(/\s+/).filter(Boolean).map((w) => w[0]).join('').toUpperCase().slice(0, 2) || '?';
}

function visitLine(v) {
  if (!v) return '';
  if (v.next) return `Next visit ${moment(v.next).format('D MMM')}`;
  if (v.last) {
    const m = moment(v.last);
    return `Last visit ${m.isSame(moment(), 'year') ? m.format('D MMM') : m.format('MMM YYYY')}`;
  }
  return '';
}

/** Soonest next visit first, then the most recent last visit, then the rest. */
function byVisit(visits) {
  const key = (book) => {
    const v = visits && visits[book];
    if (v && v.next) return [0, new Date(v.next).getTime()];
    if (v && v.last) return [1, -new Date(v.last).getTime()];
    return [2, 0];
  };
  return (a, b) => {
    const ka = key(a.book), kb = key(b.book);
    return ka[0] - kb[0] || ka[1] - kb[1];
  };
}

/** "Who shared it, who can see it" for someone else's box. */
function otherCaption(o, biz) {
  if (o.book === 'salon') return `Shared with you by ${biz || 'the team'} · visible to ${teamOf(biz)}`;
  const who = whoFromLabel(o.label) || 'a stylist';
  return `Shared with you by ${who} · visible to ${who} and ${teamOf(biz)}`;
}

const SOURCE_LABEL = {
  restore: 'Restored',
  add: 'Added with the client',
  merge: 'From a client merge',
  merge_rollback: 'From undoing a merge',
  legacy: 'Carried over',
  import: 'Imported',
  upload: 'Imported',
};

function fmtWhen(at) {
  if (!at) return '';
  const m = moment(at);
  if (!m.isValid()) return '';
  return m.isSame(moment(), 'year') ? m.format('D MMM, HH:mm') : m.format('D MMM YYYY, HH:mm');
}

const LockIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);
const PeopleIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const PencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const LEGACY_BOOK = '__legacy__';

class ClientNotes extends Component {
  constructor(props) {
    super(props);
    this.state = {
      notes: props.notes || null,
      legacy: false,
      legacyNote: props.legacyNote || '',
      loading: !props.notes,
      editing: {},   // book → true
      text: {},      // book → current textarea text
      saving: {},    // book → true
      restored: {},  // book → true when an unsaved draft was brought back
      errors: {},    // book → message
      confirm: null, // { title, text, okLabel, danger, onOk }
      historyOpen: false,
      stylistsOpen: false,
      openStylist: null, // book expanded in the drill-in
      visits: null,      // { [book]: { last, next } } — loaded on drill-in
      history: [],
      historyLoading: false,
      historyError: '',
      restoringId: null,
    };
    this._mounted = false;
  }

  componentDidMount() {
    this._mounted = true;
    window.addEventListener('beforeunload', this.onBeforeUnload);
    this.load();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.clientId !== this.props.clientId) {
      this.setState({ notes: this.props.notes || null, legacy: false, legacyNote: this.props.legacyNote || '', editing: {}, text: {}, saving: {}, restored: {}, errors: {}, historyOpen: false, history: [], stylistsOpen: false, openStylist: null, visits: null });
      this.load();
      return;
    }
    // Parent refetched the client and handed us fresher notes: take them,
    // except for boxes being edited (typed text always wins).
    if (prevProps.notes !== this.props.notes && this.props.notes) {
      this.setState({ notes: this.props.notes });
    }
    if (prevProps.legacyNote !== this.props.legacyNote && this.state.legacy) {
      this.setState({ legacyNote: this.props.legacyNote || '' });
    }
  }

  componentWillUnmount() {
    this._mounted = false;
    window.removeEventListener('beforeunload', this.onBeforeUnload);
  }

  onBeforeUnload = (e) => {
    if (!this.hasUnsaved()) return undefined;
    e.preventDefault();
    e.returnValue = 'You have an unsaved note.';
    return e.returnValue;
  }

  // ── data ────────────────────────────────────────────────────────────────

  load = () => {
    const { clientId } = this.props;
    if (!clientId) return;
    const forId = clientId;
    clientService.getClientNotes(clientId)
      .then((res) => {
        if (!this._mounted || this.props.clientId !== forId) return;
        if (res && res.notes) {
          this.setState({ notes: res.notes, legacy: false, loading: false }, this.restoreDrafts);
        } else {
          this.toLegacy(res && res.note);
        }
      })
      .catch(() => {
        if (!this._mounted || this.props.clientId !== forId) return;
        // Old BE, or a refusal: keep whatever the client payload gave us.
        if (this.state.notes) this.setState({ loading: false }, this.restoreDrafts);
        else this.toLegacy();
      });
  }

  toLegacy = (note) => {
    this.setState({ legacy: true, loading: false, legacyNote: note !== undefined && note !== null ? note : (this.props.legacyNote || '') }, this.restoreDrafts);
  }

  /** Re-open any box with an unsaved draft from an earlier visit. */
  restoreDrafts = () => {
    const { clientId } = this.props;
    const boxes = this.boxes();
    const editing = {}; const text = {}; const restored = {};
    boxes.forEach((b) => {
      if (!b.editable) return;
      const d = readDraft(clientId, b.book);
      if (d !== null && d !== (b.text || '')) {
        editing[b.book] = true; text[b.book] = d; restored[b.book] = true;
      } else if (d !== null) {
        clearDraft(clientId, b.book);
      }
    });
    if (Object.keys(editing).length) {
      this.setState((s) => ({ editing: { ...s.editing, ...editing }, text: { ...s.text, ...text }, restored: { ...s.restored, ...restored } }));
    }
  }

  /** The boxes to show, in order: yours first, then everyone else's. */
  boxes() {
    if (this.state.legacy) {
      return [{ book: LEGACY_BOOK, label: 'Notes', text: this.state.legacyNote || '', editable: !!this.props.canEditLegacy, caption: '', mine: true }];
    }
    const n = this.state.notes;
    if (!n) return [];
    const out = [];
    if (n.mine) {
      out.push({
        book: n.mine.book,
        label: n.mine.book === 'salon' ? (n.mine.label || businessBookLabel(n.businessName)) : 'Your notes',
        text: n.mine.text || '',
        editable: n.mine.editable !== false,
        caption: mineCaption(n.mine, n.businessName),
        privateNote: n.mine.visibleTo === 'only_you',
        updatedAt: n.mine.updatedAt,
        mine: true,
      });
    }
    (n.others || []).forEach((o) => {
      // A box with nothing in it that you can't write in has nothing to say.
      if (!o.editable && !(o.text || '').trim()) return;
      out.push({
        book: o.book,
        label: o.label || (o.book === 'salon' ? businessBookLabel(n.businessName) : 'Notes'),
        text: o.text || '',
        editable: !!o.editable,
        caption: otherCaption(o, n.businessName),
        updatedAt: o.updatedAt,
        mine: false,
      });
    });
    return out;
  }

  /** Readable notes in stylists' own books (not yours, not the business's). */
  stylistBoxes() {
    return this.boxes().filter((b) => !b.mine && b.book !== 'salon' && b.book !== LEGACY_BOOK && (b.text || '').trim());
  }

  privateBooks() {
    const n = this.state.notes;
    return (!this.state.legacy && n && Array.isArray(n.privateBooks)) ? n.privateBooks : [];
  }

  openStylists = () => {
    this.setState({ stylistsOpen: true, openStylist: null });
    if (this.state.visits) return;
    const forId = this.props.clientId;
    clientService.getClientNoteVisits(forId)
      .then((r) => { if (this._mounted && this.props.clientId === forId) this.setState({ visits: (r && r.visits) || {} }); })
      .catch(() => { if (this._mounted) this.setState({ visits: {} }); });
  }

  hasUnsaved() {
    const boxes = this.boxes();
    return boxes.some((b) => this.state.editing[b.book] && (this.state.text[b.book] || '') !== (b.text || ''));
  }

  // ── editing ─────────────────────────────────────────────────────────────

  startEdit = (box) => {
    const d = readDraft(this.props.clientId, box.book);
    this.setState((s) => ({
      editing: { ...s.editing, [box.book]: true },
      text: { ...s.text, [box.book]: d !== null ? d : (box.text || '') },
      restored: { ...s.restored, [box.book]: d !== null && d !== (box.text || '') },
      errors: { ...s.errors, [box.book]: '' },
    }));
  }

  onType = (book, value) => {
    writeDraft(this.props.clientId, book, value);
    this.setState((s) => ({ text: { ...s.text, [book]: value }, errors: { ...s.errors, [book]: '' } }));
  }

  endEdit = (book) => {
    clearDraft(this.props.clientId, book);
    this.setState((s) => {
      const editing = { ...s.editing }; delete editing[book];
      const text = { ...s.text }; delete text[book];
      const restored = { ...s.restored }; delete restored[book];
      return { editing, text, restored };
    });
  }

  cancelEdit = (box) => {
    const typed = this.state.text[box.book] || '';
    if (typed === (box.text || '')) { this.endEdit(box.book); return; }
    this.setState({
      confirm: {
        title: 'Discard your changes?',
        text: "What you've typed in this note won't be saved.",
        okLabel: 'Discard',
        danger: true,
        onOk: () => this.endEdit(box.book),
      },
    });
  }

  save = (box) => {
    const { clientId } = this.props;
    const value = this.state.text[box.book] || '';
    this.setState((s) => ({ saving: { ...s.saving, [box.book]: true }, errors: { ...s.errors, [box.book]: '' } }));

    const done = (res) => {
      if (!this._mounted) return;
      this.setState((s) => ({ saving: { ...s.saving, [box.book]: false } }));
      this.endEdit(box.book);
      if (this.state.legacy) this.setState({ legacyNote: value });
      else if (res && res.notes) this.setState({ notes: res.notes });
      toast.success('Note saved');
      if (this.props.onSaved) this.props.onSaved(res || { note: value });
    };
    // The draft stays in sessionStorage on failure: nothing typed is lost.
    const fail = (err) => {
      if (!this._mounted) return;
      this.setState((s) => ({
        saving: { ...s.saving, [box.book]: false },
        errors: { ...s.errors, [box.book]: (err && err.message) || "Couldn't save the note. Your text is still here — try again." },
      }));
    };

    if (this.state.legacy) {
      hairdresserService.editClient({ userId: clientId, hairdresserId: localStorage.getItem('userId'), note: value })
        .then((r) => {
          const d = r && r.data;
          if (d && (d.errorCode || d.statusCode === 0)) throw new Error((d.error && d.error.responseMessage) || d.messages || "Couldn't save the note.");
          done({ note: value });
        })
        .catch(fail);
      return;
    }
    clientService.saveClientNote(clientId, value, box.book).then(done).catch(fail);
  }

  // ── history ─────────────────────────────────────────────────────────────

  openHistory = () => {
    this.setState({ historyOpen: true, historyLoading: true, historyError: '' });
    clientService.getClientNoteHistory(this.props.clientId, 50)
      .then((res) => { if (this._mounted) this.setState({ history: (res && res.history) || [], historyLoading: false }); })
      .catch((err) => { if (this._mounted) this.setState({ history: [], historyLoading: false, historyError: (err && err.message) || "Couldn't load the history." }); });
  }

  closeHistory = () => this.setState({ historyOpen: false, confirm: null });

  askRestore = (entry, which) => {
    const undo = which === 'before';
    const target = undo ? entry.before : entry.after;
    const editingThatBook = this.state.editing[entry.book] && (this.state.text[entry.book] || '') !== this.currentText(entry.book);
    this.setState({
      confirm: {
        title: undo ? 'Undo this change?' : 'Restore this version?',
        text: (target || '').trim()
          ? `${entry.label || 'This note'} will go back to this version. The current text stays in the history, so you can change your mind.${editingThatBook ? ' Your unsaved changes to this note will be discarded.' : ''}`
          : `${entry.label || 'This note'} will be cleared. The current text stays in the history, so you can change your mind.`,
        okLabel: undo ? 'Undo' : 'Restore',
        onOk: () => this.restore(entry, which),
      },
    });
  }

  currentText(book) {
    const b = this.boxes().find((x) => x.book === book);
    return b ? (b.text || '') : '';
  }

  restore = (entry, which) => {
    this.setState({ restoringId: entry._id });
    clientService.restoreClientNote(this.props.clientId, entry._id, which)
      .then((res) => {
        if (!this._mounted) return;
        this.endEdit(entry.book);
        this.setState({ notes: (res && res.notes) || this.state.notes, restoringId: null });
        toast.success('Note restored');
        if (this.props.onSaved) this.props.onSaved(res);
        this.openHistory();
      })
      .catch((err) => {
        if (!this._mounted) return;
        this.setState({ restoringId: null });
        toast.error((err && err.message) || "Couldn't restore that version.");
      });
  }

  // ── render ──────────────────────────────────────────────────────────────

  renderCaption(box) {
    if (!box.caption) return null;
    const Icon = box.privateNote ? LockIcon : PeopleIcon;
    return (
      <p className={'cn-caption' + (box.privateNote ? ' cn-caption--private' : '')}>
        <Icon />
        <span>{box.caption}</span>
      </p>
    );
  }

  renderBox(box, showLabel) {
    const editing = !!this.state.editing[box.book];
    const saving = !!this.state.saving[box.book];
    const error = this.state.errors[box.book];
    const { compact } = this.props;
    const value = this.state.text[box.book] || '';
    const dirty = editing && value !== (box.text || '');

    return (
      <section key={box.book} className={'cn-box' + (compact ? ' cn-box--compact' : '')}>
        {showLabel && (
          <header className="cn-box-head">
            <h4 className="cn-box-title">{box.label}</h4>
            {box.updatedAt && !editing && (box.text || '').trim() && (
              <span className="cn-box-meta">Updated {fmtWhen(box.updatedAt)}</span>
            )}
          </header>
        )}
        {this.renderCaption(box)}

        {editing ? (
          <div className="cn-editor">
            {this.state.restored[box.book] && (
              <p className="cn-restored">We kept what you were typing last time. Save it or discard it.</p>
            )}
            <textarea
              className="cn-textarea"
              value={value}
              onChange={(e) => this.onType(box.book, e.target.value)}
              placeholder={box.mine ? 'Add notes about this client…' : `Add to ${box.label.toLowerCase()}…`}
              disabled={saving}
              rows={compact ? 4 : 7}
              autoFocus
            />
            {error && <p className="cn-error">{error}</p>}
            <div className="cn-actions">
              <button type="button" className="cn-btn cn-btn--ghost" onClick={() => this.cancelEdit(box)} disabled={saving}>Cancel</button>
              <button type="button" className="cn-btn cn-btn--primary" onClick={() => this.save(box)} disabled={saving || !dirty}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        ) : (box.text || '').trim() ? (
          <div className="cn-read">
            <p className="cn-text">{box.text}</p>
            {box.editable && (
              <button type="button" className="cn-edit-btn" onClick={() => this.startEdit(box)} aria-label={`Edit ${box.label.toLowerCase()}`}>
                <PencilIcon />
              </button>
            )}
          </div>
        ) : box.editable ? (
          <button type="button" className="cn-add-btn" onClick={() => this.startEdit(box)}>
            <PlusIcon /> Add a note
          </button>
        ) : null}
      </section>
    );
  }

  // ONE line for every stylist's notes, however many: never clutter the tab.
  renderStylistSummary() {
    const readable = this.stylistBoxes();
    const hidden = this.privateBooks();
    const total = readable.length + hidden.length;
    if (!total) return null;
    const names = readable.map((b) => whoFromLabel(b.label) || b.label).concat(hidden.map((p) => p.name));
    const shown = names.slice(0, 3);
    const more = names.length - shown.length;
    let title = `Notes from ${total === 1 ? names[0] : `${total} stylists`}`;
    let sub = '';
    if (!readable.length) { title = total === 1 ? `${names[0]} has private notes` : `Private notes from ${total} stylists`; sub = 'Only they can see what they say'; }
    else if (hidden.length) sub = `${readable.length} you can read`;
    return (
      <button type="button" className="cn-stylists-row" onClick={this.openStylists}>
        <span className="cn-avatars" aria-hidden="true">
          {shown.map((nm, i) => <span key={i} className="cn-avatar">{initialsOf(nm)}</span>)}
          {more > 0 && <span className="cn-avatar cn-avatar--more">+{more}</span>}
        </span>
        <span className="cn-stylists-text">
          <span className="cn-stylists-title">{!readable.length && <LockIcon />} {title}</span>
          {sub && <span className="cn-stylists-sub">{sub}</span>}
        </span>
        <svg className="cn-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6" /></svg>
      </button>
    );
  }

  // The drill-in: readable notes open inline; private ones are names only.
  renderStylists() {
    const { visits, openStylist } = this.state;
    const sort = byVisit(visits || {});
    const readable = this.stylistBoxes().slice().sort(sort);
    const hidden = this.privateBooks().slice().sort(sort);
    return (
      <section className="cn-hist-panel" aria-label="Stylists' notes">
        <header className="cn-hist-panel-head">
          <button type="button" className="cn-hist-back" onClick={() => this.setState({ stylistsOpen: false })} aria-label="Back to notes">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <div>
            <h3 className="cn-sheet-title">Stylists’ notes</h3>
            <p className="cn-sheet-sub">Each self-employed stylist keeps their own notes. Only the stylist can change them.</p>
          </div>
        </header>
        <div className="cn-hist-panel-body">
          {readable.length > 0 && (
            <ul className="cn-slist">
              {readable.map((b) => {
                const open = openStylist === b.book;
                const who = whoFromLabel(b.label) || b.label;
                return (
                  <li key={b.book} className={'cn-srow' + (open ? ' cn-srow--open' : '')}>
                    <button type="button" className="cn-srow-head" onClick={() => this.setState({ openStylist: open ? null : b.book })} aria-expanded={open}>
                      <span className="cn-avatar">{initialsOf(who)}</span>
                      <span className="cn-srow-who">
                        <span className="cn-srow-name">{who}</span>
                        {visits && visitLine(visits[b.book]) && <span className="cn-srow-visit">{visitLine(visits[b.book])}</span>}
                      </span>
                      <svg className="cn-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points={open ? '6 9 12 15 18 9' : '9 18 15 12 9 6'} /></svg>
                    </button>
                    <p className={'cn-srow-text' + (open ? '' : ' cn-srow-text--clamp')}>{b.text}</p>
                    {open && b.updatedAt && <p className="cn-srow-meta">Updated {fmtWhen(b.updatedAt)}</p>}
                  </li>
                );
              })}
            </ul>
          )}
          {hidden.length > 0 && (
            <>
              <h4 className="cn-sgroup"><LockIcon /> Private notes</h4>
              <p className="cn-sgroup-sub">Only these stylists can see what their notes say.</p>
              <ul className="cn-slist">
                {hidden.map((p) => (
                  <li key={p.book} className="cn-srow">
                    <div className="cn-srow-head cn-srow-head--static">
                      <span className="cn-avatar">{initialsOf(p.name)}</span>
                      <span className="cn-srow-who">
                        <span className="cn-srow-name">{p.name}</span>
                        {visits && visitLine(visits[p.book]) && <span className="cn-srow-visit">{visitLine(visits[p.book])}</span>}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </section>
    );
  }

  renderHistory() {
    if (!this.state.historyOpen) return null;
    const { history, historyLoading, historyError, restoringId } = this.state;
    const me = String(localStorage.getItem('userId') || '');
    const books = new Set(history.map((h) => h.book));
    // The newest entry per book is the current text: "Undo" (back to before)
    // reads better there than "Restore" (which would be a no-op).
    const seen = new Set();
    const latest = new Set();
    history.forEach((h) => { if (!seen.has(h.book)) { seen.add(h.book); latest.add(String(h._id)); } });

    return (
      // In the panel itself (not a modal over it): Back returns to the notes.
      <section className="cn-hist-panel" aria-label="Note history">
          <header className="cn-hist-panel-head">
            <button type="button" className="cn-hist-back" onClick={this.closeHistory} aria-label="Back to notes">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            <div>
              <h3 className="cn-sheet-title">Note history</h3>
              <p className="cn-sheet-sub">Every change is kept. Restoring makes a new change, so nothing is ever lost.</p>
            </div>
          </header>
          <div className="cn-hist-panel-body">
            {historyLoading ? (
              <p className="cn-empty">Loading…</p>
            ) : historyError ? (
              <p className="cn-error">{historyError}</p>
            ) : history.length === 0 ? (
              <p className="cn-empty">No changes yet.</p>
            ) : (
              <ol className="cn-history">
                {history.map((h) => {
                  const who = h.byUserId && String(h.byUserId) === me ? 'You' : (h.byName || (h.source === 'client' ? 'The client' : 'Someone at the salon'));
                  const isLatest = latest.has(String(h._id));
                  const which = isLatest ? 'before' : 'after';
                  const src = SOURCE_LABEL[h.source];
                  return (
                    <li key={h._id} className="cn-hist">
                      <div className="cn-hist-head">
                        <span className="cn-hist-who">{who}</span>
                        <span className="cn-hist-when">{fmtWhen(h.at)}</span>
                      </div>
                      {(books.size > 1 || src) && (
                        <div className="cn-hist-tags">
                          {books.size > 1 && <span className="cn-tag">{h.label}</span>}
                          {src && <span className="cn-tag cn-tag--soft">{src}</span>}
                        </div>
                      )}
                      {(h.before || '').trim() ? <p className="cn-hist-before">{h.before}</p> : null}
                      {(h.after || '').trim()
                        ? <p className="cn-hist-after">{h.after}</p>
                        : <p className="cn-hist-cleared">Cleared the note</p>}
                      {h.restorable && !(isLatest && (h.before || '') === (h.after || '')) && (
                        <div className="cn-hist-actions">
                          <button
                            type="button"
                            className="cn-link"
                            disabled={!!restoringId}
                            onClick={() => this.askRestore(h, which)}
                          >
                            {restoringId === h._id ? 'Restoring…' : (isLatest ? 'Undo this change' : 'Restore this version')}
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
      </section>
    );
  }

  renderConfirm() {
    const c = this.state.confirm;
    if (!c) return null;
    const close = () => this.setState({ confirm: null });
    return (
      <div className="sp-confirm-overlay cn-confirm-overlay" onClick={close}>
        <div className="sp-confirm-card" onClick={(e) => e.stopPropagation()}>
          <h3 className="sp-confirm-title">{c.title}</h3>
          <p className="sp-confirm-text">{c.text}</p>
          <div className="sp-confirm-actions">
            <button type="button" onClick={close} style={{ border: '1px solid #e5e5e5', background: '#fff', color: '#6b7280' }}>Cancel</button>
            <button
              type="button"
              onClick={() => { close(); c.onOk(); }}
              style={{ border: 'none', background: c.danger ? '#dc2626' : '#29282D', color: '#fff' }}
            >
              {c.okLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { compact } = this.props;
    if (this.state.loading && !this.state.notes) {
      return <div className={'cn-root' + (compact ? ' cn-root--compact' : '')}><p className="cn-empty">Loading notes…</p></div>;
    }
    const boxes = this.boxes();
    const nothing = boxes.length === 0;
    // Stylists' notes live behind the summary line, not as boxes here.
    const tucked = new Set(this.stylistBoxes().map((b) => b.book));
    const primary = boxes.filter((b) => !tucked.has(b.book));
    // Every book box is labelled ("Your notes", "<Business> notes", "Paul's
    // notes"); only the legacy single box goes without.
    const showLabels = !!boxes[0] && boxes[0].book !== LEGACY_BOOK;

    if (this.state.stylistsOpen) {
      return <div className={'cn-root' + (compact ? ' cn-root--compact' : '')}>{this.renderStylists()}</div>;
    }

    if (this.state.historyOpen) {
      return (
        <div className={'cn-root' + (compact ? ' cn-root--compact' : '')}>
          {this.renderHistory()}
          {this.state.confirm && ReactDOM.createPortal(this.renderConfirm(), document.body)}
        </div>
      );
    }

    return (
      <div className={'cn-root' + (compact ? ' cn-root--compact' : '')}>
        {nothing && !this.privateBooks().length ? (
          <p className="cn-empty">No notes</p>
        ) : primary.map((b) => this.renderBox(b, showLabels))}

        {this.renderStylistSummary()}

        {!this.state.legacy && !nothing && (
          <div className="cn-footer">
            <button type="button" className="cn-link" onClick={this.openHistory}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /><path d="M12 7v5l3 2" /></svg>
              History
            </button>
          </div>
        )}
        {/* Portalled: the panels animate with transforms, which would trap
            a position:fixed overlay inside them. */}
        {this.state.confirm && ReactDOM.createPortal(this.renderConfirm(), document.body)}
      </div>
    );
  }
}

export default ClientNotes;
